import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Play, Pause, SkipBack, SkipForward, Volume2, Maximize2, X } from "lucide-react";
import { useAudio, getAudioEl } from "../store/audio";
import { mediaUrl } from "../lib/api";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const AudioPlayer = () => {
  const {
    open, isPlaying, currentTime, duration, volume, fullscreen, embedTrack,
    toggle, next, prev, seek, setVolume, setFullscreen, _setTime, _setPlaying,
  } = useAudio();
  const track = useAudio((s) => s.current());

  useEffect(() => {
    const el = getAudioEl();
    if (!el) return;
    const onTime = () => _setTime(el.currentTime, el.duration || 0);
    const onEnd = () => next();
    const onPlay = () => _setPlaying(true);
    const onPause = () => _setPlaying(false);
    const onError = () => { _setPlaying(false); toast.error("Lecture impossible pour cette piste."); };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onTime);
    el.addEventListener("ended", onEnd);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onTime);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("error", onError);
    };
  }, [next, _setTime, _setPlaying]);

  if (!open || !track) return null;
  const isEmbed = !!embedTrack;
  const pct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <>
      <AnimatePresence>
        {fullscreen && (
          <motion.div data-testid="audio-fullscreen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="fixed inset-0 z-[60] bg-[#080808] flex flex-col">
            <div className="flex justify-between items-center px-6 py-5 border-b eff-line">
              <span className="eff-label">EFFERVESCENCE SOUND</span>
              <button data-testid="audio-fullscreen-close" onClick={() => setFullscreen(false)}><X className="w-5 h-5" strokeWidth={1.2} /></button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center px-6 gap-8">
              {track.cover && <img src={mediaUrl(track.cover)} alt="" className="w-64 h-64 md:w-80 md:h-80 object-cover border eff-line" />}
              <div className="text-center">
                <div className="font-display text-3xl">{track.title}</div>
                <div className="text-[#a6a6a6] mt-2 text-sm">{track.artist}</div>
              </div>
              {isEmbed ? (
                <iframe title="embed-full" src={embedTrack.embed_url} className="w-full max-w-xl h-40 border eff-line" allow="autoplay" />
              ) : (
                <div className="w-full max-w-xl">
                  <input type="range" className="eff-range w-full" min={0} max={duration || 0} value={currentTime} onChange={(e) => seek(Number(e.target.value))} />
                  <div className="flex justify-between text-xs text-[#a6a6a6] mt-2"><span>{fmt(currentTime)}</span><span>{fmt(duration)}</span></div>
                </div>
              )}
              <div className="flex items-center gap-10">
                <button onClick={prev}><SkipBack className="w-6 h-6" strokeWidth={1.2} /></button>
                {!isEmbed && (
                  <button onClick={toggle} className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center">
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                  </button>
                )}
                <button onClick={next}><SkipForward className="w-6 h-6" strokeWidth={1.2} /></button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div data-testid="audio-player-bar" initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-0 left-0 right-0 z-50 border-t eff-line bg-[#080808]/90 backdrop-blur-2xl">
        <div className="h-[2px] w-full bg-[#292929]"><div className="h-full bg-white" style={{ width: `${pct}%` }} /></div>
        <div className="max-w-[1180px] mx-auto px-4 md:px-7 h-16 md:h-20 flex items-center gap-4">
          {track.cover && <img src={mediaUrl(track.cover)} alt="" className="w-10 h-10 md:w-12 md:h-12 object-cover border eff-line" />}
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate">{track.title}</div>
            <div className="text-xs text-[#a6a6a6] truncate">{track.artist}{isEmbed && <span className="ml-2 uppercase tracking-widest text-[10px]">· embed</span>}</div>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <button data-testid="audio-prev" onClick={prev}><SkipBack className="w-5 h-5" strokeWidth={1.2} /></button>
            {!isEmbed && (
              <button data-testid="audio-toggle" onClick={toggle} className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center">
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
            )}
            <button data-testid="audio-next" onClick={next}><SkipForward className="w-5 h-5" strokeWidth={1.2} /></button>
          </div>

          {isEmbed ? (
            <>
              <div className="flex md:hidden items-center gap-5">
                <button data-testid="audio-prev-mobile" onClick={prev}><SkipBack className="w-5 h-5" strokeWidth={1.2} /></button>
                <button data-testid="audio-next-mobile" onClick={next}><SkipForward className="w-5 h-5" strokeWidth={1.2} /></button>
              </div>
              <div className="hidden lg:block flex-[2] max-w-md">
                <iframe title="embed" src={embedTrack.embed_url} className="w-full h-12 border eff-line" allow="autoplay" />
              </div>
            </>
          ) : (
            <>
              <div className="hidden lg:flex items-center gap-3 flex-[1.5] max-w-md">
                <span className="text-xs text-[#a6a6a6] w-9 text-right">{fmt(currentTime)}</span>
                <input type="range" className="eff-range flex-1" min={0} max={duration || 0} value={currentTime} onChange={(e) => seek(Number(e.target.value))} />
                <span className="text-xs text-[#a6a6a6] w-9">{fmt(duration)}</span>
              </div>
              <div className="hidden xl:flex items-center gap-2 w-32">
                <Volume2 className="w-4 h-4 text-[#a6a6a6]" strokeWidth={1.2} />
                <input type="range" className="eff-range flex-1" min={0} max={1} step={0.01} value={volume} onChange={(e) => setVolume(Number(e.target.value))} />
              </div>
              <button data-testid="audio-toggle-mobile" onClick={toggle} className="md:hidden w-10 h-10 rounded-full bg-white text-black flex items-center justify-center">
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
            </>
          )}
          <button data-testid="audio-expand" onClick={() => setFullscreen(true)} className="ml-1"><Maximize2 className="w-4 h-4 text-[#a6a6a6]" strokeWidth={1.2} /></button>
        </div>
      </motion.div>
    </>
  );
};
